import { Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImageUpload } from "@/components/admin/ImageUpload";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API, errMsg, imgUrl } from "@/lib/api";

export default function AdminGallery() {
  const [cats, setCats] = useState(null);
  const [images, setImages] = useState(null);
  const [catDialog, setCatDialog] = useState(null);
  const [imgDialog, setImgDialog] = useState(null);
  const [catForm, setCatForm] = useState({ name: "", order: 0, active: true });
  const [imgForm, setImgForm] = useState({ url: "", caption: "", category_id: "", order: 0, active: true });

  const load = () => {
    API.get("/admin/gallery/categories").then((r) => setCats(r.data)).catch((e) => toast.error(errMsg(e)));
    API.get("/admin/gallery/images").then((r) => setImages(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, []);

  const saveCat = async () => {
    if (!catForm.name.trim()) return toast.error("Name is required");
    try {
      if (catDialog?.id) await API.patch(`/admin/gallery/categories/${catDialog.id}`, { ...catForm, order: Number(catForm.order) || 0 });
      else await API.post("/admin/gallery/categories", { ...catForm, order: Number(catForm.order) || 0 });
      toast.success("Gallery category saved");
      setCatDialog(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveImg = async () => {
    if (!imgForm.url) return toast.error("Please upload or choose an image");
    try {
      const payload = { ...imgForm, order: Number(imgForm.order) || 0 };
      if (imgDialog?.id) await API.patch(`/admin/gallery/images/${imgDialog.id}`, payload);
      else await API.post("/admin/gallery/images", payload);
      toast.success("Image saved");
      setImgDialog(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const catName = (id) => (cats || []).find((c) => c.id === id)?.name || "—";

  return (
    <div data-testid="admin-gallery-page">
      <h1 className="font-display text-3xl tracking-tight mb-6">Gallery</h1>
      <Tabs defaultValue="images">
        <TabsList data-testid="gallery-tabs">
          <TabsTrigger value="images" data-testid="tab-images">Images</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-gallery-categories">Categories</TabsTrigger>
        </TabsList>

        <TabsContent value="images" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button
              className="rounded-full"
              onClick={() => {
                setImgForm({ url: "", caption: "", category_id: cats?.[0]?.id || "", order: (images || []).length + 1, active: true });
                setImgDialog({});
              }}
              data-testid="add-gallery-image-btn"
            >
              <Plus className="mr-2 h-4 w-4" /> Add image
            </Button>
          </div>
          {!images ? (
            <Skeleton className="h-64 rounded-xl" />
          ) : images.length === 0 ? (
            <p className="text-muted-foreground text-center py-16 border rounded-xl" data-testid="gallery-images-empty">No gallery images yet.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-gallery-grid">
              {images.map((img) => (
                <div key={img.id} className="relative group rounded-xl overflow-hidden border" data-testid={`admin-gallery-image-${img.id}`}>
                  <img src={imgUrl(img.url)} alt={img.caption || ""} className={`aspect-square w-full object-cover ${!img.active ? "opacity-40" : ""}`} />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3 pt-8">
                    <p className="text-white text-xs truncate">{img.caption || "—"}</p>
                    <p className="text-white/60 text-[10px]">{catName(img.category_id)}{!img.active ? " · hidden" : ""}</p>
                  </div>
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="icon" variant="secondary" className="h-7 w-7"
                      onClick={() => {
                        setImgForm({ url: img.url, caption: img.caption || "", category_id: img.category_id || "", order: img.order || 0, active: !!img.active });
                        setImgDialog(img);
                      }}
                      data-testid={`edit-gallery-image-${img.id}`} aria-label="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="icon" variant="destructive" className="h-7 w-7"
                      onClick={async () => {
                        try {
                          await API.delete(`/admin/gallery/images/${img.id}`);
                          toast.success("Image deleted");
                          load();
                        } catch (e) { toast.error(errMsg(e)); }
                      }}
                      data-testid={`delete-gallery-image-${img.id}`} aria-label="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button
              className="rounded-full"
              onClick={() => {
                setCatForm({ name: "", order: (cats || []).length + 1, active: true });
                setCatDialog({});
              }}
              data-testid="add-gallery-category-btn"
            >
              <Plus className="mr-2 h-4 w-4" /> Add category
            </Button>
          </div>
          {!cats ? (
            <Skeleton className="h-40 rounded-xl" />
          ) : (
            <div className="space-y-2" data-testid="gallery-categories-list">
              {cats.map((c) => (
                <Card key={c.id} data-testid={`gallery-category-${c.id}`}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <p className={`flex-1 font-medium ${!c.active ? "line-through text-muted-foreground" : ""}`}>{c.name}</p>
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => {
                        setCatForm({ name: c.name, order: c.order || 0, active: !!c.active });
                        setCatDialog(c);
                      }}
                      data-testid={`edit-gallery-category-${c.id}`} aria-label="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-destructive" data-testid={`delete-gallery-category-${c.id}`} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete "{c.name}"?</AlertDialogTitle>
                          <AlertDialogDescription>All images in this gallery category will also be removed.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={async () => {
                            try {
                              await API.delete(`/admin/gallery/categories/${c.id}`);
                              toast.success("Category deleted");
                              load();
                            } catch (e) { toast.error(errMsg(e)); }
                          }}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!catDialog} onOpenChange={(o) => !o && setCatDialog(null)}>
        <DialogContent data-testid="gallery-category-dialog">
          <DialogHeader><DialogTitle>{catDialog?.id ? "Edit category" : "New gallery category"}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} data-testid="gallery-category-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Sort order</Label>
              <Input type="number" value={catForm.order} onChange={(e) => setCatForm({ ...catForm, order: e.target.value })} data-testid="gallery-category-order-input" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={!!catForm.active} onCheckedChange={(v) => setCatForm({ ...catForm, active: v })} data-testid="gallery-category-active-switch" />
            </div>
            <Button className="w-full rounded-full" onClick={saveCat} data-testid="save-gallery-category-btn">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!imgDialog} onOpenChange={(o) => !o && setImgDialog(null)}>
        <DialogContent data-testid="gallery-image-dialog">
          <DialogHeader><DialogTitle>{imgDialog?.id ? "Edit image" : "Add gallery image"}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Image *</Label>
              <ImageUpload value={imgForm.url} onChange={(v) => setImgForm({ ...imgForm, url: v })} label="gallery image" testId="gallery-image-upload" />
            </div>
            <div className="space-y-2">
              <Label>Caption</Label>
              <Input value={imgForm.caption} onChange={(e) => setImgForm({ ...imgForm, caption: e.target.value })} data-testid="gallery-image-caption-input" />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={imgForm.category_id} onValueChange={(v) => setImgForm({ ...imgForm, category_id: v })}>
                <SelectTrigger data-testid="gallery-image-category-select"><SelectValue placeholder="Choose category" /></SelectTrigger>
                <SelectContent>
                  {(cats || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Sort order</Label>
              <Input type="number" value={imgForm.order} onChange={(e) => setImgForm({ ...imgForm, order: e.target.value })} data-testid="gallery-image-order-input" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Visible</Label>
              <Switch checked={!!imgForm.active} onCheckedChange={(v) => setImgForm({ ...imgForm, active: v })} data-testid="gallery-image-active-switch" />
            </div>
            <Button className="w-full rounded-full" onClick={saveImg} data-testid="save-gallery-image-btn">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
